import { useEffect } from 'react';

interface JsonLdSchema {
  '@context': string;
  '@type': string;
  [key: string]: unknown;
}

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  jsonLd?: JsonLdSchema[];
}

const BASE_NAME = 'Taller Mecánico Kevin Alberto';
const GEO_REGION = 'ES-MA';
const GEO_PLACE = 'Ronda, Málaga';
const GEO_POS = '36.7450;-5.1679';

const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  jsonLd,
}: SEOProps) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const canonicalUrl = `${siteUrl}${canonical}`;
  const resolvedOgTitle = ogTitle || title;
  const resolvedOgDesc = ogDescription || description;

  useEffect(() => {
    // Remove previous JSON-LD scripts injected by SEO component
    document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());

    if (jsonLd && jsonLd.length > 0) {
      jsonLd.forEach((schema, idx) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', String(idx));
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());
    };
  }, [jsonLd]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content={new Date().toISOString().split('T')[0]} />

      {/* Geo */}
      <meta name="geo.region" content={GEO_REGION} />
      <meta name="geo.placename" content={GEO_PLACE} />
      <meta name="geo.position" content={GEO_POS} />
      <meta name="ICBM" content="36.7450, -5.1679" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:site_name" content={BASE_NAME} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDesc} />
      <meta property="og:url" content={canonicalUrl} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDesc} />
    </>
  );
};

export default SEO;
